import React, { useState, useEffect } from 'react';
import { FaSearch, FaEye, FaEdit, FaTrash, FaSpinner, FaIdCard, FaEnvelope, FaPhone, FaUser, FaTimes, FaDownload } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import EditUserModal from './EditUserModal';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const AdminUsers = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [downloadingPDF, setDownloadingPDF] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
    });
    const [searchFilters, setSearchFilters] = useState({
        name: '',
        email: '',
        mobile: '',
        user_id: ''
    });
    const [appliedFilters, setAppliedFilters] = useState({
        name: '',
        email: '',
        mobile: '',
        user_id: ''
    });
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [editSection, setEditSection] = useState('personal');

    // Only fetch when page changes or applied filters change (not when typing)
    useEffect(() => {
        fetchUsers();
    }, [pagination.page, appliedFilters]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            const searchParams = new URLSearchParams();
            searchParams.append('page', pagination.page);
            searchParams.append('limit', 20);
            
            if (appliedFilters.name) searchParams.append('name', appliedFilters.name);
            if (appliedFilters.email) searchParams.append('email', appliedFilters.email);
            if (appliedFilters.mobile) searchParams.append('mobile', appliedFilters.mobile);
            if (appliedFilters.user_id) searchParams.append('user_id', appliedFilters.user_id);
            
            const response = await axios.get(`https://app.bihebari.com.np/api/admin/users?${searchParams.toString()}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.success) {
                setUsers(response.data.data);
                setPagination(response.data.pagination);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Error loading users');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setSearchFilters({ ...searchFilters, [name]: value });
    };

    const handleSearch = () => {
        setAppliedFilters({ ...searchFilters });
        setPagination({ ...pagination, page: 1 });
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const removeFilter = (filterName) => {
        setSearchFilters({ ...searchFilters, [filterName]: '' });
        setAppliedFilters({ ...appliedFilters, [filterName]: '' });
        setPagination({ ...pagination, page: 1 });
    };

    const clearAllFilters = () => {
        setSearchFilters({
            name: '',
            email: '',
            mobile: '',
            user_id: ''
        });
        setAppliedFilters({
            name: '',
            email: '',
            mobile: '',
            user_id: ''
        });
        setPagination({ ...pagination, page: 1 });
    };

    const handlePaymentStatusChange = async (userId, currentStatus) => {
        const newStatus = currentStatus === 'paid' ? 'pending' : 'paid';
        try {
            const token = localStorage.getItem('adminToken');
            await axios.put(`https://app.bihebari.com.np/api/admin/users/${userId}/payment`, 
                { pay_status: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Payment status updated');
            fetchUsers();
        } catch (error) {
            toast.error('Error updating status');
        }
    };

    const handleDeleteUser = async (userId) => {
        if (window.confirm('Are you sure you want to delete this user?')) {
            try {
                const token = localStorage.getItem('adminToken');
                await axios.delete(`https://app.bihebari.com.np/api/admin/users/${userId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('User deleted successfully');
                fetchUsers();
            } catch (error) {
                toast.error('Error deleting user');
            }
        }
    };

    const viewUserDetails = (userId) => {
    window.open(`/admin/users/${userId}`, '_blank', 'noopener,noreferrer');
    };

    const openEditModal = (user, section) => {
        setSelectedUser(user);
        setEditSection(section);
        setModalOpen(true);
    };

    const handleUpdateComplete = () => {
        fetchUsers();
    };

    const downloadAgreementPDF = async (user) => {
        setDownloadingPDF(user.id);
        
        const tempDiv = document.createElement('div');
        tempDiv.style.position = 'absolute';
        tempDiv.style.top = '-9999px';
        tempDiv.style.left = '-9999px';
        tempDiv.style.width = '800px';
        tempDiv.style.backgroundColor = 'white';
        tempDiv.style.padding = '40px';
        tempDiv.style.fontFamily = 'Arial, "Noto Sans Nepali", sans-serif';
        
        const currentDate = new Date().toLocaleDateString('ne-NP', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
        
        const userName = user?.name || '_____________';
        const userShowId = user?.id || '_____________';
        const userEmail = user?.email || 'Not provided';
        const userPhone = user?.contact || 'Not provided';
        const signature = userName;
        
        tempDiv.innerHTML = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <style>
                    * {
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                    }
                    body {
                        font-family: 'Arial', 'Noto Sans Nepali', sans-serif;
                        line-height: 1.5;
                        color: #333;
                    }
                    .header {
                        background: linear-gradient(135deg, #dc2626, #1e3a8a);
                        padding: 30px;
                        text-align: center;
                        color: white;
                        margin-bottom: 30px;
                    }
                    .header h1 {
                        font-size: 28px;
                        margin-bottom: 10px;
                    }
                    .header p {
                        font-size: 18px;
                    }
                    .user-info {
                        margin-bottom: 20px;
                        padding: 15px;
                        background: #f3f4f6;
                        border-radius: 8px;
                    }
                    .user-info div {
                        margin-bottom: 8px;
                    }
                    .section-title {
                        font-size: 18px;
                        font-weight: bold;
                        color: #dc2626;
                        margin: 20px 0 15px 0;
                        padding-bottom: 5px;
                        border-bottom: 2px solid #dc2626;
                    }
                    .clause {
                        margin-bottom: 15px;
                        padding: 10px;
                        background: #f9fafb;
                        border-radius: 5px;
                    }
                    .clause-nep {
                        font-size: 14px;
                        font-weight: bold;
                        margin-bottom: 8px;
                        color: #1f2937;
                    }
                    .clause-eng {
                        font-size: 12px;
                        color: #6b7280;
                        border-top: 1px solid #e5e7eb;
                        padding-top: 8px;
                    }
                    .refund-box {
                        background: #fff6e8;
                        border-left: 4px solid #f97316;
                        padding: 15px;
                        margin: 15px 0;
                        border-radius: 8px;
                    }
                    .declaration {
                        background: #fef3c7;
                        padding: 15px;
                        margin: 15px 0;
                        border-radius: 8px;
                        border: 1px solid #fbbf24;
                    }
                    .signature-area {
                        margin: 25px 0;
                        padding: 15px;
                        border-top: 1px solid #d1d5db;
                    }
                    .footer {
                        margin-top: 30px;
                        padding-top: 15px;
                        text-align: center;
                        font-size: 10px;
                        color: #9ca3af;
                        border-top: 1px solid #e5e7eb;
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <h1>BIHEBARI</h1>
                    <p>Declaration & Agreement | घोषणा र सम्झौता</p>
                </div>
                
                <div class="user-info">
                    <div><strong>Date (मिति):</strong> ${currentDate}</div>
                    <div><strong>Name (नाम):</strong> ${userName}</div>
                    <div><strong>Show ID (शो आईडी):</strong> BB${userShowId}</div>
                    <div><strong>Email (इमेल):</strong> ${userEmail}</div>
                    <div><strong>Phone (फोन):</strong> ${userPhone}</div>
                </div>
                
                <div class="section-title">📜 Terms & Conditions (सर्तहरू)</div>
                
                <div class="clause">
                    <div class="clause-nep">१. विहेवारीले हजुरहरुले उपलब्ध गराएको विवरण र मापदण्ड अनुसार आफ्नो डाटाबेसबाट उपयुक्त पात्र खोज्ने जिम्मेवारी लिन्छ। यो खोज कार्यका लागि लाग्ने समय, प्रविधि र कर्मचारीको मेहेनत बापत शुरुमै दर्ता शुल्क रु ४००० लाग्नेछ।</div>
                    <div class="clause-eng">Bihebari takes responsibility to search suitable matches from our database based on criteria. An upfront registration fee of NPR 4,000 is charged for technology, staff efforts and search process.</div>
                </div>
                
                <div class="clause">
                    <div class="clause-nep">२. दर्ता शुल्क बुझाइसके पछि ४ महिना भित्र कुनै पनि बहानामा "रकम फिर्ता" (Refund) माग्न पाइने छैन।</div>
                    <div class="clause-eng">Once registration fee is paid, a "Refund" cannot be claimed on any excuses within 4 months.</div>
                </div>
                
                <div class="clause">
                    <div class="clause-nep">३. गोपनीयतालाई ध्यानमा राखी हजुरहरुको व्यक्तिगत विवरण र फोटोहरू पूर्ण रूपमा सुरक्षित राखिनेछ र केवल उपयुक्त र गम्भीर पात्रहरूलाई मात्र देखाइनेछ।</div>
                    <div class="clause-eng">We guarantee full privacy for your personal information and photographs; they will only be disclosed to compatible and genuine matches.</div>
                </div>
                
                <div class="clause">
                    <div class="clause-nep">४. विवाह निश्चित भएपछि सेवा शुल्क ३०,००० लाग्ने छ।</div>
                    <div class="clause-eng">Service Charge of Rs. 30,000/- is liable after marriage is fixed.</div>
                </div>
                
                <div class="clause">
                    <div class="clause-nep">५. यदी विहेवारीबाट विवाह भएको खण्डमा विहे भएको १ महिनासम्म पनि सेवा शुल्क नतिरेमा वा शुल्क तिर्नुपर्छ भनी जानकारी गराउँदा पनि सम्पर्क विहीन भएमा, संस्थाले मेरो फोटो र विवरण सार्वजनिक गर्न पाउने पूर्ण अधिकार राख्दछ। यस्तो अवस्थामा संस्थाले चालेको कदम विरुद्ध मैले कुनै पनि कानुनी उजुरी वा दाबी गर्ने छैन ।</div>
                    <div class="clause-eng">If you get married via Bihebari and service fee not settled within one month of marriage, or unreachable regarding payment, the institution reserves right to publish my photo and details publicly. I waive legal action/complaints.</div>
                </div>
                
                <div class="section-title">💰 Refund Policy (पैसा फिर्ता नीति)</div>
                
                <div class="refund-box">
                    <div class="clause-nep">यदि दर्ता भएको ४ महिनासम्म पनि 'विहेवारी' ले हजुरको मापदण्ड अनुसारको एक (१) जोडीसँग पनि कुरा गराउन सकेन भने निम्न सर्तमा पैसा फिर्ता:</div>
                    <div class="clause-nep">• फिर्ता रकम: प्रशासनिक खर्च र मेहेनत बापत रु. २००० कट्टा गरी रु. २,०००/- (दुई हजार) फिर्ता हुनेछ ।</div>
                    <div class="clause-nep">• फिर्ता समय: ४ महिना पूरा भएको ७ दिनभित्र ।</div>
                    <div class="clause-nep">🔔 नोट: यदि उपयुक्त पात्रसँग कुराकानी अगाडि बढेको छ भने फिर्ता लागू हुँदैन। रु. ४००० मा अधिकतम ४ जना सम्भावित पात्रसँग सम्पर्क गर्न सकिन्छ। नम्बर आदानप्रदान भएपछि मात्र 'म्याच' गणना हुन्छ।</div>
                    <div class="clause-eng" style="margin-top: 10px;"><strong>English:</strong> If Bihebari cannot facilitate conversation with even a single partner (matching criteria) within 4 months: Refund amount NPR 2,000 after deducting NPR 2,000 admin cost. Refund within 7 days after 4 month period. Note: Refund void if suitable match conversation started. Up to 4 matches within registration fee. Contact exchange = Match counted.</div>
                </div>
                
                <div class="section-title">⚖️ Legal Declaration (कानुनी स्वघोषणा)</div>
                
                <div class="declaration">
                    <div class="clause-nep">म श्री "${userName}" BB${userShowId} ले माथि दिएका सबै विवरण कानुनी रूपमा सत्य छ । यदि भविष्यमा कुनै कानुनी त्रुटि भेटिएमा त्यसको पूर्ण जिम्मेवार म आफै हुनेछु र विहेवारीलाई कुनै दोष दिने छैन।</div>
                    <div class="clause-eng" style="margin-top: 10px;">I, Mr./Ms. "${userName}" BB${userShowId}, hereby declare that all information provided above is legally true. If any legal discrepancy is found in the future, I shall be fully responsible and will not hold Bihebari liable.</div>
                </div>
                
                <div class="signature-area">
                    <div><strong>Signature (हस्ताक्षर):</strong> ${signature}</div>
                    <div style="margin-top: 10px;"><strong>Signed Name (हस्ताक्षरकर्ताको नाम):</strong> ${signature}</div>
                    <div style="margin-top: 15px; font-style: italic; font-size: 12px;">I hereby confirm that I have read, understood, and agreed to all the above terms, conditions, refund policy, and legal declaration.</div>
                    <div style="margin-top: 5px; font-style: italic; font-size: 12px;">मैले माथिका सबै सर्तहरू, पैसा फिर्ता नीति, र कानुनी स्वघोषणा पढेको, बुझेको, र स्वीकार गरेको पुष्टि गर्दछु।</div>
                </div>
                
                <div class="footer">
                    Generated on: ${currentDate} | Bihebari Agreement - Legally Binding Document
                </div>
            </body>
            </html>
        `;
        
        document.body.appendChild(tempDiv);
        
        try {
            const canvas = await html2canvas(tempDiv, {
                scale: 2,
                logging: false,
                useCORS: true,
                backgroundColor: '#ffffff'
            });
            
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });
            
            const imgWidth = 210;
            const pageHeight = 297;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let heightLeft = imgHeight;
            let position = 0;
            
            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
            
            while (heightLeft > 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }
            
            pdf.save(`Bihebari_Agreement_${userName.replace(/\s/g, '_')}_BB${userShowId}.pdf`);
            toast.success(`Agreement downloaded for ${userName}`);
        } catch (error) {
            console.error('PDF generation error:', error);
            toast.error('Failed to generate PDF. Please try again.');
        } finally {
            document.body.removeChild(tempDiv);
            setDownloadingPDF(null);
        }
    };

    // Count active filters
    const activeFilterCount = Object.values(appliedFilters).filter(v => v).length;

    if (loading) {
        return (
            <div className="flex justify-center items-center h-96">
                <FaSpinner className="text-4xl text-indigo-600 animate-spin" />
            </div>
        );
    }

    return (
        <div>
            <h1 className="text-2xl font-bold text-gray-800 mb-6">Manage Users</h1>
            
            {/* Search Filters */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <FaUser className="inline mr-1 text-gray-400" /> Name
                        </label>
                        <input
                            type="text"
                            name="name"
                            value={searchFilters.name}
                            onChange={handleFilterChange}
                            onKeyPress={handleKeyPress}
                            placeholder="Search by name (partial match)..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <FaEnvelope className="inline mr-1 text-gray-400" /> Email
                        </label>
                        <input
                            type="email"
                            name="email"
                            value={searchFilters.email}
                            onChange={handleFilterChange}
                            onKeyPress={handleKeyPress}
                            placeholder="Search by email (partial match)..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <FaPhone className="inline mr-1 text-gray-400" /> Mobile Number
                        </label>
                        <input
                            type="tel"
                            name="mobile"
                            value={searchFilters.mobile}
                            onChange={handleFilterChange}
                            onKeyPress={handleKeyPress}
                            placeholder="Search by mobile (partial match, e.g., 98510)..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <FaIdCard className="inline mr-1 text-gray-400" /> User ID
                        </label>
                        <input
                            type="text"
                            name="user_id"
                            value={searchFilters.user_id}
                            onChange={handleFilterChange}
                            onKeyPress={handleKeyPress}
                            placeholder="Search by user ID (exact match)..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>
                </div>
                
                {/* Search Button */}
                <div className="mt-4 flex justify-end">
                    <button
                        onClick={handleSearch}
                        className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center gap-2"
                    >
                        <FaSearch />
                        <span>Search</span>
                    </button>
                </div>
                
                {/* Active Filters */}
                {activeFilterCount > 0 && (
                    <div className="mt-4 pt-4 border-t flex flex-wrap items-center gap-2">
                        <span className="text-sm text-gray-600">Active filters:</span>
                        {appliedFilters.name && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                                Name: {appliedFilters.name}
                                <button onClick={() => removeFilter('name')} className="hover:text-blue-900">
                                    <FaTimes size={12} />
                                </button>
                            </span>
                        )}
                        {appliedFilters.email && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                                Email: {appliedFilters.email}
                                <button onClick={() => removeFilter('email')} className="hover:text-green-900">
                                    <FaTimes size={12} />
                                </button>
                            </span>
                        )}
                        {appliedFilters.mobile && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                                Mobile: {appliedFilters.mobile}
                                <button onClick={() => removeFilter('mobile')} className="hover:text-purple-900">
                                    <FaTimes size={12} />
                                </button>
                            </span>
                        )}
                        {appliedFilters.user_id && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">
                                User ID: {appliedFilters.user_id}
                                <button onClick={() => removeFilter('user_id')} className="hover:text-orange-900">
                                    <FaTimes size={12} />
                                </button>
                            </span>
                        )}
                        <button onClick={clearAllFilters} className="text-red-500 hover:text-red-700 text-sm ml-2">
                            Clear all
                        </button>
                    </div>
                )}
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mobile</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Registered</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {users.map((user) => (
                                <tr key={user.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 text-sm font-mono text-gray-900">
                                        <span className="bg-gray-100 px-2 py-1 rounded">#{user.id}</span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center text-white text-sm font-bold">
                                                {user.name?.charAt(0) || 'U'}
                                            </div>
                                            <span className="ml-3 text-sm font-medium text-gray-900">{user.name}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600">
                                        {user.email || '-'}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600">
                                        {user.contact || '-'}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600">
                                        {user.city_name ? `${user.city_name}, ` : ''}
                                        {user.state_name ? `${user.state_name}, ` : ''}
                                        {user.country_name || '-'}
                                    </td>
                                    <td className="px-6 py-4">
                                        <button
                                            onClick={() => handlePaymentStatusChange(user.id, user.pay_status)}
                                            className={`px-3 py-1 text-xs rounded-full font-medium transition ${
                                                user.pay_status === 'paid' 
                                                    ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                                                    : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                                            }`}
                                        >
                                            {user.pay_status === 'paid' ? '✓ Paid' : '⏳ Pending'}
                                        </button>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600">
                                        {new Date(user.created).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => viewUserDetails(user.id)}
                                                className="text-blue-600 hover:text-blue-800 transition p-1"
                                                title="View Details"
                                            >
                                                <FaEye size={18} />
                                            </button>
                                            <button
                                                onClick={() => openEditModal(user, 'personal')}
                                                className="text-green-600 hover:text-green-800 transition p-1"
                                                title="Edit User"
                                            >
                                                <FaEdit size={18} />
                                            </button>
                                            <button
                                                onClick={() => downloadAgreementPDF(user)}
                                                disabled={downloadingPDF === user.id}
                                                className={`transition p-1 ${downloadingPDF === user.id ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-800'}`}
                                                title="Download Agreement"
                                            >
                                                {downloadingPDF === user.id ? (
                                                    <FaSpinner className="animate-spin" size={18} />
                                                ) : (
                                                    <FaDownload size={18} />
                                                )}
                                            </button>
                                            <button
                                                onClick={() => handleDeleteUser(user.id)}
                                                className="text-red-600 hover:text-red-800 transition p-1"
                                                title="Delete User"
                                            >
                                                <FaTrash size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                    <div className="flex justify-center space-x-2 py-4 border-t">
                        <button
                            onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                            disabled={pagination.page === 1}
                            className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition"
                        >
                            Previous
                        </button>
                        <span className="px-4 py-2 text-gray-600">
                            Page {pagination.page} of {pagination.totalPages}
                        </span>
                        <button
                            onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                            disabled={pagination.page === pagination.totalPages}
                            className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            {/* Results Count */}
            {users.length > 0 && (
                <div className="mt-4 text-center text-sm text-gray-500">
                    Showing {users.length} of {pagination.total} users
                </div>
            )}

            {/* Edit User Modal */}
            <EditUserModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                user={selectedUser}
                section={editSection}
                onUpdate={handleUpdateComplete}
            />
        </div>
    );
};

export default AdminUsers;